import { MEASURE_METRIC_KEY, MEASURE_LABELS, MEASURES } from "./aabiConstants";
import { formatGrowth } from "./aabiFormatters";

// The single view-model boundary (§9.2): selected-measure keys, recomputed
// shares, portfolio/trend transforms and deterministic Key Drivers all live
// here so charts, tables and cards can never disagree.

export const selectedMetricKey = (measure) =>
  MEASURE_METRIC_KEY[measure] ?? MEASURE_METRIC_KEY[MEASURES.CURRENT];

// Compact currency is display-only (§5.4); full precision stays available for
// tooltips and accessible labels via formatCurrency.
export const formatCompactCurrency = (value) => {
  const numeric = Number(value ?? 0);
  const safe = Number.isFinite(numeric) ? numeric : 0;
  const abs = Math.abs(safe);
  const sign = safe < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return `${sign}$${abs.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

const shareOf = (part, whole) =>
  whole > 0 ? part / whole : 0;

// Portfolio rows (§6.2): share is recomputed from the SELECTED measure against
// the scoped total — the contract's Current-Sales-only `shareOfParentRevenue`
// is never reused in Realized mode (§6.2, §8.1).
export const buildPortfolioRows = (rows, totals, measure) => {
  const metricKey = selectedMetricKey(measure);
  const total = totals?.[metricKey] ?? 0;

  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      ...row,
      metricKey,
      selectedRevenue: row?.[metricKey] ?? 0,
      priorRevenue: row?.prior?.[metricKey] ?? 0,
      growth: row?.growth?.[metricKey] ?? null,
      selectedShare: shareOf(row?.[metricKey] ?? 0, total),
    }))
    .sort((a, b) => b.selectedRevenue - a.selectedRevenue);
};

// Key Drivers (§6.3): deterministic rules over the loaded child rows. Minimum
// materiality is 5% share of the selected-revenue total. Anything not
// supported by the data is omitted rather than overstated.
const MATERIALITY_THRESHOLD = 0.05;

export const computeKeyDrivers = (portfolioRows) => {
  if (!portfolioRows.length) return null;

  const totalSelectedRevenue = portfolioRows.reduce(
    (sum, row) => sum + row.selectedRevenue,
    0,
  );
  if (totalSelectedRevenue <= 0) return null;

  const material = portfolioRows.filter(
    (row) => row.selectedShare >= MATERIALITY_THRESHOLD,
  );

  const largest = [...portfolioRows].sort(
    (a, b) => b.selectedRevenue - a.selectedRevenue,
  )[0];

  const growers = material
    .filter((row) => row.growth != null && row.growth > 0)
    .sort((a, b) => b.growth - a.growth);
  const fastestGrowing = growers[0] ?? null;

  const decliners = material
    .filter((row) => row.growth != null && row.growth < 0)
    .sort((a, b) => a.growth - b.growth);
  const largestDecliner = decliners[0] ?? null;

  const topThreeShare = [...portfolioRows]
    .slice(0, 3)
    .reduce((sum, row) => sum + row.selectedShare, 0);

  return {
    largest,
    fastestGrowing,
    largestDecliner,
    topThreeShare,
    materialityThreshold: MATERIALITY_THRESHOLD,
  };
};

// Trend summary sentence (§6.1): one deterministic sentence describing the
// selected measure's movement across the period.
export const buildTrendSummary = (trend, measure) => {
  const metricKey = selectedMetricKey(measure);
  const series = trend?.currentSeries ?? [];
  const priorSeries = trend?.priorSeries ?? [];
  if (!series.length) return null;

  const total = series.reduce((sum, b) => sum + (b[metricKey] ?? 0), 0);
  const priorTotal = priorSeries.reduce(
    (sum, b) => sum + (b[metricKey] ?? 0),
    0,
  );
  const growth = priorTotal > 0 ? (total - priorTotal) / priorTotal : null;

  const measureLabel = MEASURE_LABELS[measure];
  if (growth === null) {
    return `${measureLabel} shows no prior-period comparison for this scope.`;
  }
  const direction =
    growth > 0 ? "up" : growth < 0 ? "down" : "flat";
  const percent = Math.abs(growth * 100).toFixed(1);

  const peak = series.reduce(
    (best, bucket) => ((bucket[metricKey] ?? 0) > (best[metricKey] ?? 0) ? bucket : best),
    series[0],
  );
  const peakLabel = peak?.[metricKey] > 0 ? `, peaking in ${peak.label}` : "";

  return `${measureLabel} is ${direction} ${percent}%${growth === 0 ? "" : peakLabel} over this period.`;
};

export const growthLabel = formatGrowth;
